import React from 'react'
import { UpdateExternalContactForm } from './UpdateExternalContactForm'
import { getPanelInfos } from './hooks'
import { IntegrationServiceId } from '../../common/integrations'
import { filterIgnoreFieldsFromKeys, getAllKeysInData } from './utils'
import { ClickNavigationSection } from '../../components/NavigationWithSideBar/ClickNavigation'

interface SegmentedExternalContactUpdateClickNavigationSectionProps {
  serviceId: IntegrationServiceId
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  panel: { [key: string]: any }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata: { [key: string]: any } | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contactSchema: { [key: string]: any }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  contactValues: { [key: string]: any }
  panelKey: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  previousContactValues: { [key: string]: any }
  onKeySelected?: (selectedKey: string, isUnselect: boolean) => void
  rootFieldId?: string
  hidePreviousValues?: boolean
  hideRootFieldHeader?: boolean

  segmentLoadingStates: Record<string, boolean>
  selectedKeys: string[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialValues: { [key: string]: any }
  sectionError: { hasErrors: boolean; errorCount: number }
  onValidationChange: (key: string, hasErrors: boolean, errorCount: number) => void
}

export const SegmentedExternalContactUpdateClickNavigationSection = ({
  serviceId,
  panel,
  metadata,
  contactSchema,
  contactValues,
  panelKey,
  previousContactValues,
  onKeySelected,
  rootFieldId = 'contactValues',
  hidePreviousValues = false,
  hideRootFieldHeader = false,
  segmentLoadingStates,
  selectedKeys,
  initialValues,
  sectionError,
  onValidationChange,
}: SegmentedExternalContactUpdateClickNavigationSectionProps): ClickNavigationSection | null => {
  const panelValues =
    contactValues && typeof contactValues === 'object' && panelKey in contactValues ? (contactValues as Record<string, unknown>)[panelKey] : undefined
  const isSegmentLoading = segmentLoadingStates[panelKey] ?? false

  const { errorMessages } = getPanelInfos({
    panel,
    metadata: metadata ?? {},
    contactSchema,
    contactValues: contactValues || {},
    key: panelKey,
    previousContactValues,
  })

  const panelRootPath = `${rootFieldId}.${panelKey}`

  const allKeysInPanel = getAllKeysInData(panelValues, panelRootPath)
  const selectedKeysInPanel = allKeysInPanel.filter((k) => selectedKeys.includes(k))
  const validSelectedKeysCount = filterIgnoreFieldsFromKeys(serviceId, selectedKeysInPanel).length

  const initialPanelData = initialValues[panelKey]
  const allInitialKeys = getAllKeysInData(initialPanelData, panelRootPath)
  const validInitialKeysCount = filterIgnoreFieldsFromKeys(serviceId, allInitialKeys).length

  const baseLabel = panel.name || panelKey
  const label = `${baseLabel} (${validSelectedKeysCount})`
  const currentSectionError = sectionError || { hasErrors: false, errorCount: 0 }

  const isGloballyLoading = false
  const hasData = validInitialKeysCount > 0 || validSelectedKeysCount > 0
  const isDisabled = !isGloballyLoading && !isSegmentLoading && !hasData

  return {
    id: panelKey,
    label: label,
    disabled: isDisabled,
    hasError: errorMessages.length > 0 || currentSectionError.hasErrors,
    isLoading: isSegmentLoading,
    content: () => {
      return (
        <UpdateExternalContactForm
          serviceId={serviceId}
          rootFieldId={panelRootPath}
          contactSchema={contactSchema[panelKey].schema}
          contactValues={contactValues[panelKey]}
          previousContactValues={previousContactValues[panelKey].values}
          requiredSteps={panel?.['required_operation_steps'] ?? {}}
          metadata={metadata}
          onKeySelected={onKeySelected}
          hidePreviousValues={hidePreviousValues}
          hideRootFieldHeader={hideRootFieldHeader}
          onValidationChange={(hasErrors, errorCount) => onValidationChange(panelKey, hasErrors, errorCount)}
          enableExplainer={false}
        />
      )
    },
  }
}
